import Swal from 'sweetalert2';
import ModalBase from '@/components/base/ModalBase';
import type { Invoice, InvoiceItem } from '@/mocks/superAdminInvoices';
import { invoiceStatusLabels, invoiceItemTypeLabels } from '@/mocks/superAdminInvoices';

interface InvoiceDetailModalProps {
  open: boolean;
  invoice: Invoice;
  onClose: () => void;
  onMarkPaid: (inv: Invoice) => void;
  onMarkOverdue: (inv: Invoice) => void;
  onCancel: (inv: Invoice) => void;
  onDuplicate: (inv: Invoice) => void;
  onPrint: (inv: Invoice) => void;
  onNavigate: (path: string) => void;
}

function formatCurrency(n: number): string {
  return `$${n.toFixed(2)}`;
}

export default function InvoiceDetailModal({
  open, invoice, onClose,
  onMarkPaid, onMarkOverdue, onCancel, onDuplicate, onPrint, onNavigate,
}: InvoiceDetailModalProps) {
  const canMarkPaid = invoice.status === 'draft' || invoice.status === 'pending';
  const canMarkOverdue = invoice.status === 'draft' || invoice.status === 'pending';
  const canCancel = invoice.status === 'draft' || invoice.status === 'pending' || invoice.status === 'overdue';

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={invoice.number}
      subtitle={invoice.radioName}
      statusLabel={invoiceStatusLabels[invoice.status]}
      size="lg"
    >
      <div className="space-y-5">
        {/* Info grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Radio</p>
            <p className="text-foreground-800 text-xs font-semibold">{invoice.radioName}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Frecuencia</p>
            <p className="text-foreground-800 text-xs font-semibold">{invoice.frequency}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Plan</p>
            <p className="text-foreground-800 text-xs font-semibold">{invoice.planName}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">IVA</p>
            <p className="text-foreground-800 text-xs font-semibold">{(invoice.taxRate * 100).toFixed(0)}%</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Emisión</p>
            <p className="text-foreground-800 text-xs font-semibold">{invoice.issuedAt}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Vence</p>
            <p className={`text-xs font-semibold ${invoice.status === 'overdue' ? 'text-red-500' : 'text-foreground-800'}`}>{invoice.dueDate}</p>
          </div>
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Pago</p>
            <p className="text-foreground-800 text-xs font-semibold">{invoice.paidAt || '—'}</p>
          </div>
        </div>

        {/* Concepto */}
        {invoice.concepto && (
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Concepto</p>
            <p className="text-foreground-800 text-xs font-semibold">{invoice.concepto}</p>
          </div>
        )}

        {/* Items table */}
        <div>
          <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-2">Ítems</p>
          <div className="bg-background-100 rounded-xl overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-200/50">
                  <th className="px-3 py-2 text-foreground-400 text-[9px] font-semibold uppercase">Concepto</th>
                  <th className="px-3 py-2 text-foreground-400 text-[9px] font-semibold uppercase text-center">Cant.</th>
                  <th className="px-3 py-2 text-foreground-400 text-[9px] font-semibold uppercase text-right">P.Unit.</th>
                  <th className="px-3 py-2 text-foreground-400 text-[9px] font-semibold uppercase text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {invoice.items.map((it: InvoiceItem) => (
                  <tr key={it.id}>
                    <td className="px-3 py-2.5 text-foreground-800 text-xs font-medium">{it.description}</td>
                    <td className="px-3 py-2.5 text-center text-foreground-600 text-xs">{it.quantity}</td>
                    <td className="px-3 py-2.5 text-right text-foreground-600 text-xs">${it.unitPrice.toFixed(2)}</td>
                    <td className="px-3 py-2.5 text-right text-foreground-800 text-xs font-semibold">${it.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-background-200">
                  <td colSpan={3} className="px-3 py-2 text-right text-foreground-500 text-[10px] font-medium">Subtotal</td>
                  <td className="px-3 py-2 text-right text-foreground-800 text-xs font-semibold">{formatCurrency(invoice.subtotal)}</td>
                </tr>
                {invoice.discount > 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right text-foreground-500 text-[10px] font-medium">Descuento</td>
                    <td className="px-3 py-2 text-right text-emerald-600 text-xs font-semibold">-{formatCurrency(invoice.discount)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-right text-foreground-500 text-[10px] font-medium">IVA ({(invoice.taxRate * 100).toFixed(0)}%)</td>
                  <td className="px-3 py-2 text-right text-foreground-800 text-xs font-semibold">{formatCurrency(invoice.tax)}</td>
                </tr>
                <tr className="border-t-2 border-foreground-200">
                  <td colSpan={3} className="px-3 py-2 text-right text-foreground-800 text-xs font-bold">TOTAL</td>
                  <td className="px-3 py-2 text-right text-foreground-800 text-sm font-bold">{formatCurrency(invoice.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Notas</p>
            <p className="text-foreground-700 text-xs">{invoice.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-background-200">
          <button onClick={() => onPrint(invoice)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-printer-line text-sm"></i>Imprimir
          </button>
          <button onClick={() => { onDuplicate(invoice); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-file-copy-line text-sm"></i>Duplicar
          </button>
          {canMarkPaid && (
            <button onClick={() => { onMarkPaid(invoice); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-check-line text-sm"></i>Marcar pagada
            </button>
          )}
          {canMarkOverdue && (
            <button onClick={() => { onMarkOverdue(invoice); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-alert-line text-sm"></i>Marcar vencida
            </button>
          )}
          {canCancel && (
            <button onClick={() => { onCancel(invoice); onClose(); }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-400 text-white text-xs font-medium hover:bg-foreground-500 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-forbid-line text-sm"></i>Cancelar
            </button>
          )}
        </div>
      </div>
    </ModalBase>
  );
}